import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useAuth } from '@clerk/clerk-react';
import { QRCodeCanvas } from 'qrcode.react';
import { AlertTriangle, CheckCircle2 } from 'lucide-react';

const SOURCE_OPTIONS = [
    { value: 'youtube', label: 'YouTube Video' },
    { value: 'email', label: 'Email' },
    { value: 'channel', label: 'Channel' },
    { value: 'community', label: 'Community' },
    { value: 'ad', label: 'Ad' },
    { value: 'blog', label: 'Blog' },
    { value: 'discord', label: 'Discord' },
    { value: 'facebook', label: 'Facebook' },
    { value: 'instagram', label: 'Instagram' },
    { value: 'leadMagnet', label: 'Lead Magnet' },
    { value: 'linkedin', label: 'LinkedIn' },
    { value: 'live', label: 'Live Stream' },
    { value: 'podcast', label: 'Podcast' },
    { value: 'telegram', label: 'Telegram' },
    { value: 'threads', label: 'Threads' },
    { value: 'tiktok', label: 'TikTok' },
    { value: 'twitter', label: 'Twitter' },
    { value: 'webinar', label: 'Webinar' },
    { value: 'website', label: 'Website' },
];

const getSourceParam = (source) => source === 'email' ? 'rtemail' : source;

const buildQrUrl = (landingPageUrl, qrId, source = 'youtube', name = null) => {
    const url = new URL(landingPageUrl);

    if (!['http:', 'https:'].includes(url.protocol)) {
        throw new Error('Unsupported URL protocol');
    }

    if (source === 'youtube') {
        url.searchParams.set('qr', qrId);
    } else if (name) {
        url.searchParams.set(getSourceParam(source), name);
    }

    return url.toString();
};

const withTrackedUrl = (qrCode) => ({
    ...qrCode,
    trackedUrl: buildQrUrl(qrCode.landingPageUrl, qrCode.id, qrCode.source, qrCode.name),
});

const isYoutubeQrConnected = (qrCode) => (
    qrCode.source === 'youtube'
    && typeof qrCode.name === 'string'
    && qrCode.name.trim().length > 0
);

const QrCodeGenerator = ({
    username,
    landingPages = [],
    onSaveLandingPage,
    onRemoveLandingPage,
}) => {
    const [landingPageUrl, setLandingPageUrl] = useState('');
    const [source, setSource] = useState('youtube');
    const [sourceName, setSourceName] = useState('');
    const [qrCodes, setQrCodes] = useState([]);
    const [error, setError] = useState('');
    const [copySuccess, setCopySuccess] = useState('');
    const [youtubeUrls, setYoutubeUrls] = useState({});
    const [loading, setLoading] = useState(true);
    const [savingVideoId, setSavingVideoId] = useState(null);
    const [showQrExplanation, setShowQrExplanation] = useState(false);
    const [showLandingDropdown, setShowLandingDropdown] = useState(false);
    const [showNewLandingPageInput, setShowNewLandingPageInput] = useState(false);
    const [newLandingPageValue, setNewLandingPageValue] = useState('');
    const landingDropdownRef = useRef(null);
    const { getToken } = useAuth();
    const SERVER_URL = process.env.REACT_APP_SERVER_URL;

    useEffect(() => {
        if (!username) return;

        const fetchQrCodes = async () => {
            setLoading(true);
            setError('');

            try {
                const token = await getToken();
                const response = await axios.get(
                    `https://${SERVER_URL}/api/qr/${username}`,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                setQrCodes((response.data.qrCodes || []).map(withTrackedUrl));
            } catch (fetchError) {
                console.error('Could not load QR codes:', fetchError);
                setError('Could not load your saved QR codes.');
            } finally {
                setLoading(false);
            }
        };

        fetchQrCodes();
    }, [SERVER_URL, getToken, username]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (landingDropdownRef.current && !landingDropdownRef.current.contains(event.target)) {
                if (showNewLandingPageInput && newLandingPageValue.trim()) {
                    handleAddLandingPage();
                } else {
                    setShowLandingDropdown(false);
                    setShowNewLandingPageInput(false);
                }
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    });

    const handleAddLandingPage = () => {
        const newPage = newLandingPageValue.trim();
        if (!newPage) return;

        onSaveLandingPage?.(newPage);
        setLandingPageUrl(newPage);
        setNewLandingPageValue('');
        setShowNewLandingPageInput(false);
        setShowLandingDropdown(false);
    };

    const handleRemoveLandingPage = (page) => {
        onRemoveLandingPage?.(page);
        if (landingPageUrl === page) {
            setLandingPageUrl('');
        }
    };

    const handleGenerate = async (event) => {
        event.preventDefault();
        setError('');
        setCopySuccess('');

        try {
            buildQrUrl(landingPageUrl.trim(), 'validation', source, sourceName.trim());
        } catch {
            setError('Please enter a valid landing page URL, including https://');
            return;
        }

        if (source !== 'youtube' && !sourceName.trim()) {
            setError('Please enter a name for this source.');
            return;
        }

        try {
            const token = await getToken();
            const response = await axios.post(
                `https://${SERVER_URL}/api/qr/${username}`,
                {
                    landingPageUrl: landingPageUrl.trim(),
                    source,
                    name: source === 'youtube' ? null : sourceName.trim(),
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            setQrCodes((currentQrCodes) => [
                withTrackedUrl(response.data.qrCode),
                ...currentQrCodes,
            ]);
            setLandingPageUrl('');
            setSourceName('');
        } catch (createError) {
            console.error('Could not create QR code:', createError);
            setError(createError.response?.data?.error || 'Could not create the QR code.');
        }
    };

    const handleYoutubeUrlChange = (qrId, youtubeUrl) => {
        setYoutubeUrls((currentUrls) => ({ ...currentUrls, [qrId]: youtubeUrl }));
    };

    const handleConnectVideo = async (qrId) => {
        setError('');
        setSavingVideoId(qrId);

        try {
            const token = await getToken();
            const response = await axios.patch(
                `https://${SERVER_URL}/api/qr/${username}/${qrId}/video`,
                { youtubeUrl: youtubeUrls[qrId] || '' },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setQrCodes((currentQrCodes) => currentQrCodes.map((qrCode) => (
                qrCode.id === qrId
                    ? { ...qrCode, name: response.data.qrCode.name }
                    : qrCode
            )));
        } catch (connectError) {
            console.error('Could not connect YouTube video:', connectError);
            setError(connectError.response?.data?.error || 'Could not connect the YouTube video.');
        } finally {
            setSavingVideoId(null);
        }
    };

    const handleDelete = async (qrId) => {
        setError('');

        try {
            const token = await getToken();
            await axios.delete(
                `https://${SERVER_URL}/api/qr/${username}/${qrId}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setQrCodes((currentQrCodes) => currentQrCodes.filter((qrCode) => qrCode.id !== qrId));
        } catch (deleteError) {
            console.error('Could not delete QR code:', deleteError);
            setError(deleteError.response?.data?.error || 'Could not delete the QR code.');
        }
    };

    const handleCopy = async (trackedUrl, qrId) => {
        try {
            await navigator.clipboard.writeText(trackedUrl);
            setCopySuccess(qrId);
        } catch (clipboardError) {
            console.error('Could not copy QR link:', clipboardError);
        }
    };

    const handleDownload = (qrId) => {
        const canvas = document.getElementById(`qr-code-${qrId}`);
        if (!canvas) return;

        const downloadLink = document.createElement('a');
        downloadLink.download = `revtrack-qr-${qrId}.png`;
        downloadLink.href = canvas.toDataURL('image/png');
        downloadLink.click();
    };

    return (
        <div className="qr-generator-panel">
            <div className="qr-explanation">
                <div className="tracking-explanation-toggle">
                    <button
                        type="button"
                        className="tracking-explanation-button"
                        onClick={() => setShowQrExplanation((isOpen) => !isOpen)}
                    >
                        How QR codes work
                        <span className={`te-chevron${showQrExplanation ? ' te-chevron--open' : ''}`}>▼</span>
                    </button>
                </div>

                {showQrExplanation && (
                    <div className="tracking-explanation-box qr-explanation-box">
                        <h4>QR codes for YouTube videos</h4>
                        <p>
                            Choose <strong>YouTube Video</strong> to create a reusable link containing a unique QR parameter,
                            for example <code>example.com?qr=unique-id</code>. You do not need the YouTube URL when creating it.
                        </p>
                        <p>
                            After publishing the video, find the saved QR code below and paste the YouTube video URL into
                            its <strong>YouTube Video URL</strong> field. RevTrack then connects scans from that QR code to
                            the video and can show its title, views, and results in the Dashboard.
                        </p>

                        <h4>QR codes for other sources</h4>
                        <p>
                            For Instagram, email, ads, and other sources, enter a name while creating the QR code.
                            The source and name are written directly into the destination URL, for example
                            <code>example.com?instagram=launch-post</code>.
                        </p>
                        <p>
                            These QR codes do not need to be connected later and do not show a YouTube URL field.
                            Dashboard activity is attributed directly to the source and name you selected.
                        </p>
                    </div>
                )}
            </div>

            <div className="qr-generator-card">
                <h3 className="section-headline">QR Code Generator</h3>
                <p className="qr-generator-description">
                    Choose the page viewers should visit and select the source for your tracked QR code.
                </p>

                <form className="qr-generator-form" onSubmit={handleGenerate}>
                    <label htmlFor="qrLandingPage">Choose Landing Page:</label>
                    <div className="custom-dropdown" ref={landingDropdownRef}>
                        <div
                            id="qrLandingPage"
                            className="dropdown-header"
                            onClick={() => setShowLandingDropdown((isOpen) => !isOpen)}
                        >
                            {landingPageUrl || 'Select a landing page'}
                        </div>
                        {showLandingDropdown && (
                            <div className="dropdown-list">
                                {landingPages.map((page) => (
                                    <div key={page} className="dropdown-item">
                                        <span
                                            className="dropdown-item-text"
                                            onClick={() => {
                                                setLandingPageUrl(page);
                                                setShowLandingDropdown(false);
                                                setShowNewLandingPageInput(false);
                                            }}
                                            style={{
                                                wordBreak: 'break-word',
                                                whiteSpace: 'normal',
                                                overflowWrap: 'break-word',
                                                display: 'block',
                                                width: '100%',
                                                padding: '8px 0',
                                            }}
                                        >
                                            {page}
                                        </span>
                                        <button
                                            type="button"
                                            className="remove-landing-btn"
                                            onClick={(event) => {
                                                event.stopPropagation();
                                                handleRemoveLandingPage(page);
                                            }}
                                            style={{ flexShrink: 0, marginLeft: '8px' }}
                                        >
                                            ×
                                        </button>
                                    </div>
                                ))}
                                {!showNewLandingPageInput ? (
                                    <div className="dropdown-item add-new-option">
                                        <span
                                            className="dropdown-item-text"
                                            onClick={() => setShowNewLandingPageInput(true)}
                                            style={{
                                                display: 'block',
                                                width: '100%',
                                                padding: '8px 0',
                                                color: 'var(--Primary-500)',
                                                fontWeight: '600',
                                            }}
                                        >
                                            + Add new
                                        </span>
                                    </div>
                                ) : (
                                    <div className="dropdown-item new-landing-input">
                                        <input
                                            type="url"
                                            className="new-landing-page-input"
                                            value={newLandingPageValue}
                                            onChange={(event) => setNewLandingPageValue(event.target.value)}
                                            onKeyDown={(event) => {
                                                if (event.key === 'Enter') {
                                                    event.preventDefault();
                                                    handleAddLandingPage();
                                                }
                                            }}
                                            placeholder="https://example.com"
                                            autoFocus
                                            onClick={(event) => event.stopPropagation()}
                                        />
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    <label htmlFor="qrSource">Source</label>
                    <select
                        id="qrSource"
                        value={source}
                        onChange={(event) => {
                            setSource(event.target.value);
                            setSourceName('');
                        }}
                    >
                        {SOURCE_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>

                    {source !== 'youtube' && (
                        <>
                            <label htmlFor="qrSourceName">
                                Enter a name (e.g., video name, email subject, etc.):
                            </label>
                            <input
                                id="qrSourceName"
                                type="text"
                                value={sourceName}
                                onChange={(event) => setSourceName(event.target.value)}
                                placeholder="Enter a name"
                                required
                            />
                        </>
                    )}

                    <button className="qr-generate-button" type="submit" disabled={!landingPageUrl}>
                        Generate QR Code
                    </button>
                </form>

                {error && <p className="qr-error">{error}</p>}
            </div>

            <div className="qr-list-section">
                <div className="qr-list-header">
                    <h3 className="section-headline">Saved QR Codes</h3>
                    <span>{qrCodes.length} saved</span>
                </div>

                {loading ? (
                    <div className="qr-empty-state">
                        Loading QR codes...
                    </div>
                ) : qrCodes.length === 0 ? (
                    <div className="qr-empty-state">
                        Your generated QR codes will appear here.
                    </div>
                ) : (
                    <div className="qr-code-list">
                        {qrCodes.map((qrCode) => (
                            <article className="qr-code-card" key={qrCode.id}>
                                <div className="qr-code-preview">
                                    <QRCodeCanvas
                                        id={`qr-code-${qrCode.id}`}
                                        value={qrCode.trackedUrl}
                                        size={180}
                                        level="H"
                                        marginSize={4}
                                    />
                                </div>

                                <div className="qr-code-details">
                                    <div className="qr-detail-row">
                                        <span className="qr-detail-label">Tracked URL</span>
                                        <a href={qrCode.trackedUrl} target="_blank" rel="noreferrer">
                                            {qrCode.trackedUrl}
                                        </a>
                                    </div>
                                    <div className="qr-detail-row">
                                        <span className="qr-detail-label">Source</span>
                                        <span className="qr-detail-value">
                                            {SOURCE_OPTIONS.find((option) => option.value === qrCode.source)?.label || qrCode.source}
                                            {qrCode.source !== 'youtube' && qrCode.name ? `: ${qrCode.name}` : ''}
                                        </span>
                                    </div>

                                    {qrCode.source === 'youtube' && (
                                        <>
                                            <label htmlFor={`youtube-url-${qrCode.id}`}>
                                                YouTube Video URL
                                                <span className="qr-optional-label">
                                                    {qrCode.name ? `Connected: ${qrCode.name}` : 'Add later (optional)'}
                                                </span>
                                            </label>
                                            <input
                                                id={`youtube-url-${qrCode.id}`}
                                                type="url"
                                                value={youtubeUrls[qrCode.id] || ''}
                                                onChange={(event) => handleYoutubeUrlChange(qrCode.id, event.target.value)}
                                                placeholder="https://www.youtube.com/watch?v=..."
                                            />
                                            <div
                                                className={`qr-video-connection-status ${isYoutubeQrConnected(qrCode) ? 'is-connected' : 'is-not-connected'}`}
                                                role="status"
                                            >
                                                {isYoutubeQrConnected(qrCode) ? (
                                                    <>
                                                        <CheckCircle2 aria-hidden="true" size={17} strokeWidth={2.25} />
                                                        <span>QR code connected to YouTube video. Your data will be linked to the video.</span>
                                                    </>
                                                ) : (
                                                    <>
                                                        <AlertTriangle aria-hidden="true" size={17} strokeWidth={2.25} />
                                                        <span>Note: this QR code has not been connected to a YouTube video. Once you upload your video draft on YouTube, copy the URL here to ensure data is tracked properly.</span>
                                                    </>
                                                )}
                                            </div>
                                        </>
                                    )}

                                    <div className="qr-card-actions">
                                        {qrCode.source === 'youtube' && (
                                            <button
                                                type="button"
                                                onClick={() => handleConnectVideo(qrCode.id)}
                                                disabled={savingVideoId === qrCode.id || !youtubeUrls[qrCode.id]}
                                            >
                                                {savingVideoId === qrCode.id ? 'Connecting...' : 'Connect Video'}
                                            </button>
                                        )}
                                        <button type="button" onClick={() => handleDownload(qrCode.id)}>
                                            Download PNG
                                        </button>
                                        <button
                                            type="button"
                                            className="qr-secondary-button"
                                            onClick={() => handleCopy(qrCode.trackedUrl, qrCode.id)}
                                        >
                                            {copySuccess === qrCode.id ? 'Copied!' : 'Copy Link'}
                                        </button>
                                        <button
                                            type="button"
                                            className="qr-delete-button"
                                            onClick={() => handleDelete(qrCode.id)}
                                        >
                                            Delete
                                        </button>
                                    </div>
                                </div>
                            </article>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default QrCodeGenerator;
